<script setup lang="ts">
import { format, OptionsWithTZ } from "date-fns-tz";
import { Calendar as CalendarIcon } from "lucide-vue-next";
import { cs, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { computed } from "vue";
import { TimePicker } from "@/components/ui/time-picker";
import { DateValue, fromDate, getLocalTimeZone } from "@internationalized/date";
const props = defineProps<{
    modelValue: string;
    placeholder: string;
    minDate?: Date;
    locale?: "cs" | "en";
    withTime?: boolean;
}>();

const dateFnsConfig = computed((): OptionsWithTZ => {
    if (props.locale === "cs") {
        return {
            locale: cs,
            weekStartsOn: 1,
        };
    }

    return {
        weekStartsOn: 0,
        locale: enUS,
    };
});

const modelValueAsDate = computed({
    get: () => new Date(props.modelValue),
    set: (val: Date) => emit("update:modelValue", val.toISOString()),
});

const modelValueAsDateValue = computed(() => {
    return fromDate(new Date(props.modelValue), getLocalTimeZone());
});

const emit = defineEmits<{
    (e: "update:modelValue", payload: typeof props.modelValue): void;
}>();

function onUpdate(val: DateValue | undefined) {
    emit(
        "update:modelValue",
        val ? val.toDate(getLocalTimeZone()).toISOString() : "",
    );
}
</script>

<template>
    <Popover>
        <PopoverTrigger as-child>
            <Button
                :variant="'outline'"
                :class="
                    cn(
                        'w-full justify-start text-left font-normal',
                        !props.modelValue && 'text-muted-foreground',
                    )
                "
            >
                <CalendarIcon class="mr-2 h-4 w-4 text-gray-600" />
                <span class="text-gray-600">{{
                    props.modelValue
                        ? format(
                              new Date(props.modelValue),
                              "PPpp",
                              dateFnsConfig,
                          )
                        : props.placeholder
                }}</span>
            </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0">
            <Calendar
                :locale="locale || 'en-US'"
                :min-date="minDate"
                :model-value="modelValueAsDateValue"
                @update:model-value="onUpdate"
            />

            <hr />

            <div class="px-2 py-4">
                <TimePicker
                    with-seconds
                    with-labels
                    :with-period="locale === 'en'"
                    v-model:date="modelValueAsDate"
                />
            </div>
        </PopoverContent>
    </Popover>
</template>
